import { createSlice, createSelector } from "@reduxjs/toolkit";
import {
  fetchContentsThunk,
  fetchBrowseContentsPaginatedThunk,
  fetchContentByIdThunk,
  createContentThunk,
  updateContentThunk,
  deleteContentThunk,
  addLikeThunk,
  removeLikeThunk,
  addCommentThunk,
} from "./contentThunks";

const initialState = {
  items: [],
  currentItem: null,
  loading: false,
  error: null,
  lastAction: null,
  lastActionTime: null,
  lastFetchedAt: 0,
  /** Paginated browse lists (reader/writer ContentList) — separate from full-catalog `items`. */
  browse: {
    items: [],
    hasMore: true,
    loading: false,
    loadingMore: false,
    error: null,
    endCursorCreatedAt: null,
    endCursorId: null,
  },
};

const contentSlice = createSlice({
  name: "content",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.lastAction = "clearError";
      state.lastActionTime = Date.now();
    },
    clearLoading: (state) => {
      state.loading = false;
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    setCurrentItem: (state, action) => {
      state.currentItem = action.payload;
    },
    resetBrowsePagination: (state) => {
      state.browse.items = [];
      state.browse.hasMore = true;
      state.browse.loading = false;
      state.browse.loadingMore = false;
      state.browse.error = null;
      state.browse.endCursorCreatedAt = null;
      state.browse.endCursorId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContentsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContentsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload || [];
        state.error = null;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchContentsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch content";
      })
      .addCase(fetchBrowseContentsPaginatedThunk.pending, (state) => {
        if (state.browse.items.length === 0) state.browse.loading = true;
        else state.browse.loadingMore = true;
      })
      .addCase(fetchBrowseContentsPaginatedThunk.fulfilled, (state, action) => {
        state.browse.loading = false;
        state.browse.loadingMore = false;
        const payload = action.payload || {};
        const rows = payload.items || [];
        const append = !!action.meta?.arg?.append;
        state.browse.error = null;
        if (!append) {
          state.browse.items = rows;
        } else {
          const existing = new Set(state.browse.items.map((i) => i.id));
          state.browse.items = [...state.browse.items, ...rows.filter((i) => !existing.has(i.id))];
        }
        state.browse.hasMore = !!payload.hasMore;
        state.browse.endCursorCreatedAt =
          payload.endCursorCreatedAt != null ? payload.endCursorCreatedAt : null;
        state.browse.endCursorId = payload.endCursorId != null ? payload.endCursorId : null;
      })
      .addCase(fetchBrowseContentsPaginatedThunk.rejected, (state, action) => {
        state.browse.loading = false;
        state.browse.loadingMore = false;
        state.browse.error = action.payload || "Failed to fetch content";
      })
      .addCase(fetchContentByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchContentByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload || null;
        state.error = null;
      })
      .addCase(fetchContentByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch content";
      })
      .addCase(createContentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createContentThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.items.unshift(action.payload);
          state.browse.items.unshift(action.payload);
        }
        state.currentItem = action.payload || null;
      })
      .addCase(createContentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create content";
      })
      .addCase(updateContentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateContentThunk.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        if (!updated) return;
        const i = state.items.findIndex((x) => x.id === updated.id);
        if (i !== -1) state.items[i] = updated;
        const bi = state.browse.items.findIndex((x) => x.id === updated.id);
        if (bi !== -1) state.browse.items[bi] = updated;
        if (state.currentItem?.id === updated.id) state.currentItem = updated;
      })
      .addCase(updateContentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update content";
      })
      .addCase(deleteContentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteContentThunk.fulfilled, (state, action) => {
        state.loading = false;
        const id = action.payload;
        state.items = state.items.filter((x) => x.id !== id);
        state.browse.items = state.browse.items.filter((x) => x.id !== id);
        if (state.currentItem?.id === id) state.currentItem = null;
      })
      .addCase(deleteContentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete content";
      })
      .addCase(addLikeThunk.fulfilled, (state, action) => {
        const { contentId, userId } = action.payload || {};
        if (!contentId || !userId) return;
        const it = state.items.find((x) => x.id === contentId);
        if (it) {
          if (!it.likes) it.likes = {};
          it.likes[userId] = true;
        }
        const bit = state.browse.items.find((x) => x.id === contentId);
        if (bit) {
          if (!bit.likes) bit.likes = {};
          bit.likes[userId] = true;
        }
        if (state.currentItem?.id === contentId) {
          if (!state.currentItem.likes) state.currentItem.likes = {};
          state.currentItem.likes[userId] = true;
        }
      })
      .addCase(removeLikeThunk.fulfilled, (state, action) => {
        const { contentId, userId } = action.payload || {};
        if (!contentId || !userId) return;
        const it = state.items.find((x) => x.id === contentId);
        if (it?.likes) delete it.likes[userId];
        const bit = state.browse.items.find((x) => x.id === contentId);
        if (bit?.likes) delete bit.likes[userId];
        if (state.currentItem?.id === contentId && state.currentItem?.likes) {
          delete state.currentItem.likes[userId];
        }
      })
      .addCase(addCommentThunk.fulfilled, (state, action) => {
        const { contentId, commentId, ...comment } = action.payload || {};
        if (!contentId || !commentId) return;
        const it = state.items.find((x) => x.id === contentId);
        if (it) {
          if (!it.comments) it.comments = {};
          it.comments[commentId] = comment;
        }
        const bit = state.browse.items.find((x) => x.id === contentId);
        if (bit) {
          if (!bit.comments) bit.comments = {};
          bit.comments[commentId] = comment;
        }
        if (state.currentItem?.id === contentId) {
          if (!state.currentItem.comments) state.currentItem.comments = {};
          state.currentItem.comments[commentId] = comment;
        }
      });
  },
});

export const contentReducer = contentSlice.reducer;
export const { clearError, clearLoading, clearCurrentItem, setCurrentItem, resetBrowsePagination } =
  contentSlice.actions;

const selectContentState = (state) => state.content;
export const selectContents = (state) => state.content.items || [];
export const selectBrowseContents = (state) => state.content.browse?.items || [];
export const selectBrowseLoading = (state) => state.content.browse?.loading;
export const selectBrowseLoadingMore = (state) => state.content.browse?.loadingMore;
export const selectBrowseError = (state) => state.content.browse?.error;
export const selectBrowseHasMore = (state) => state.content.browse?.hasMore;
export const selectCurrentContent = (state) => state.content.currentItem;
export const selectContentLoading = (state) => state.content.loading;
export const selectContentLoadingMore = (state) => Boolean(state.content?.browse?.loadingMore);
export const selectContentError = (state) => state.content.error;
export const selectHasMore = (state) => state.content.browse?.hasMore;
export const selectLastKey = (state) => state.content.browse?.endCursorId;
/** Cursor after the current browse page (same values passed to the next `getAllContents` request). */
export const selectBrowseEndCursorCreatedAt = (state) => state.content.browse?.endCursorCreatedAt ?? null;

export const selectContentCount = createSelector(
  [selectContentState],
  (slice) => slice.items.length
);

export const selectContentById = createSelector(
  [(state) => state.content.items, (state, id) => id],
  (items, id) => items.find((x) => x.id === id)
);
